import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Alert } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { faTrash } from '@fortawesome/pro-light-svg-icons/faTrash';
import { TypedMemo } from '../../../common/types';
import { ConfirmDialog } from '../../../common/views/modals/confirmDialog/ConfirmDialog';
import { ConfirmDialogIconMark } from '../../../common/views/modals/confirmDialog/ConfirmDialogMark';
import { useRemoveAccountData } from '../../../common/views/modals/removeAccountDataModalSML/useRemoveAccountData';
import { removeAccountErrorMessage } from '../../../common/views/modals/removeAccountDataModalSML/removeAccountErrorMessage';
import { accountMessages } from './accountMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Settings tabs; each part uses its own slice of it */
import styles from './account.module.scss';
/* eslint-enable css-modules/no-unused-class */

const messages = defineMessages({
  stores: {
    id: 'app.sml.modals.remove_account.subscriptions',
    defaultMessage:
      'We cancel a plan you bought on the web at once. We cannot cancel a subscription from the App Store or Google Play: cancel it in the store before you delete your account.',
  },
});

interface Props {
  isOpen: boolean;
  email: string;
  workspaceName: string;
  onClose: () => void;
}

/** The delete confirmation (design settings-redesign b3, in the dialogs a1 frame). Confirm calls removePersonalData. */
const DeleteAccountModalComponent: React.FC<Props> = ({ isOpen, email, workspaceName, onClose }) => {
  const intl = useIntl();
  const { handleRemoveAccountData, isLoading, error } = useRemoveAccountData();
  const items = [
    intl.formatMessage(accountMessages.confirmSignIn, { email }),
    intl.formatMessage(accountMessages.confirmWorkspace, { workspace: workspaceName }),
    intl.formatMessage(accountMessages.confirmTeam),
  ];

  return (
    <ConfirmDialog
      isOpen={isOpen}
      tone="danger"
      mark={<ConfirmDialogIconMark tone="danger" icon={faTrash} />}
      title={intl.formatMessage(accountMessages.confirmTitle)}
      description={intl.formatMessage(accountMessages.confirmIntro)}
      cancelLabel={intl.formatMessage({ id: 'app.sml.settings.account.cancel', defaultMessage: 'Cancel' })}
      confirmLabel={intl.formatMessage(accountMessages.confirmCta)}
      isBusy={isLoading}
      error={error === undefined ? undefined : removeAccountErrorMessage(intl, error)}
      onConfirm={handleRemoveAccountData}
      onCancel={onClose}
      testId="delete-account-dialog"
    >
      <ul className={styles.confirmList} data-clarity-mask="true">
        {items.map((item) => (
          <li key={item} className={styles.confirmItem}>
            <FontAwesomeIcon icon={faXmark} className={styles.confirmIcon} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
      <Alert color="info" className="mb-0">
        {intl.formatMessage(messages.stores)}
      </Alert>
    </ConfirmDialog>
  );
};

export const DeleteAccountModal = TypedMemo(DeleteAccountModalComponent);
